import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import profileApi from '../../../app/api/profileApi';

const ProfileActivitiesTab = ({ auth, siteId, accountId, profileId }) => {
  const [activities, setActivities] = useState([]);
  const [allActivities, setAllActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: dayjs().startOf('day'),
    endDate: dayjs().endOf('day'),
    type: 'today'
  });

  useEffect(() => {
    if (allActivities.length === 0) {
      setLoadingActivities(true);
      profileApi.getProfileActivities(auth.user.access_token, siteId, accountId, profileId)
        .then(r => {
           setAllActivities(r.data);
        })
        .catch(err => console.error(err))
        .finally(() => setLoadingActivities(false));
    }
  }, [auth.user.access_token, siteId, accountId, profileId, allActivities.length]);

  useEffect(() => {
    // Filter locally when allActivities or dateRange changes
    if (allActivities.length > 0) {
      const filtered = allActivities.filter(activity => {
        const activityDate = dayjs(activity.eventTimeUtc);
        return activityDate.isAfter(dateRange.startDate) && activityDate.isBefore(dateRange.endDate);
      });
      setActivities(filtered);
    } else {
        setActivities([]);
    }
  }, [allActivities, dateRange]);

  const handleDateRangeChange = (type) => {
    let startDate = dayjs();
    let endDate = dayjs();

    switch (type) {
      case 'today':
        startDate = dayjs().startOf('day');
        endDate = dayjs().endOf('day');
        break;
      case 'week':
        startDate = dayjs().startOf('week');
        endDate = dayjs().endOf('week');
        break;
      case 'lastWeek':
        startDate = dayjs().subtract(1, 'week').startOf('week');
        endDate = dayjs().subtract(1, 'week').endOf('week');
        break;
      case 'custom':
        // Keep current dates for custom, just switch mode
        startDate = dateRange.startDate;
        endDate = dateRange.endDate;
        break;
      default:
        break;
    }
    setDateRange({ startDate, endDate, type });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <ButtonGroup variant="outlined" aria-label="date range button group">
          <Button 
            variant={dateRange.type === 'today' ? 'contained' : 'outlined'}
            onClick={() => handleDateRangeChange('today')}
          >
            Today
          </Button>
          <Button 
            variant={dateRange.type === 'week' ? 'contained' : 'outlined'}
            onClick={() => handleDateRangeChange('week')}
          >
            This Week
          </Button>
          <Button 
            variant={dateRange.type === 'lastWeek' ? 'contained' : 'outlined'}
            onClick={() => handleDateRangeChange('lastWeek')}
          >
            Last Week
          </Button>
          <Button 
            variant={dateRange.type === 'custom' ? 'contained' : 'outlined'}
            onClick={() => handleDateRangeChange('custom')}
          >
            Custom
          </Button>
        </ButtonGroup>
        
        {dateRange.type === 'custom' && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <DatePicker
              label="Start Date"
              value={dateRange.startDate}
              onChange={(newValue) => setDateRange(prev => ({ ...prev, startDate: newValue }))}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="End Date"
              value={dateRange.endDate}
              onChange={(newValue) => setDateRange(prev => ({ ...prev, endDate: newValue }))}
              slotProps={{ textField: { size: 'small' } }}
            />
          </Box>
        )}
      </Box>

      {loadingActivities ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Event Time</TableCell>
                <TableCell>Activity Type</TableCell>
                <TableCell>Site</TableCell>
                <TableCell>Zone</TableCell>
                <TableCell>Time In</TableCell>
                <TableCell>Time Out</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activities.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    backgroundColor: row.activityType === 'GATE_ENTRY' ? 'rgba(76, 175, 80, 0.08)' : 
                                   row.activityType === 'GATE_EXIT' ? 'rgba(211, 47, 47, 0.08)' : 'inherit'
                  }}
                >
                  <TableCell component="th" scope="row">
                    {dayjs(row.eventTimeUtc).format('DD/MM/YYYY HH:mm:ss')}
                  </TableCell>
                  <TableCell>{row.activityType}</TableCell>
                  <TableCell>{row.siteName}</TableCell>
                  <TableCell>{row.zoneName}</TableCell>
                  <TableCell>{row.timeInUtc ? dayjs(row.timeInUtc).format('HH:mm:ss') : '-'}</TableCell>
                  <TableCell>{row.timeOutUtc ? dayjs(row.timeOutUtc).format('HH:mm:ss') : '-'}</TableCell>
                </TableRow>
              ))}
              {activities.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No recent activities found for selected date range
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ProfileActivitiesTab;

